/**
 * The AI composer's draft, turned into estimating state.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THIS IS A TRUST BOUNDARY.
 * The draft response is model-authored JSON that has crossed a server, and it
 * is about to become line-item state in a quoting tool. The backend validates
 * it hard; this module validates it again, because the failure mode here is a
 * bad number reaching a customer's quote.
 *
 * The rules that make that safe:
 *   1. Field-by-field whitelist assignment onto `createLineItem()`. Never
 *      `{...createLineItem(), ...raw}` — an unknown key must not survive.
 *   2. Every number is coerced and clamped. A NaN, an Infinity, a negative
 *      hour figure or a string becomes a defined, in-range default.
 *   3. `unit_cost` is forced to 0. The composer never prices anything, and we
 *      do not "helpfully" fill a price in from the material catalog either.
 *   4. Malformed sub-records are DROPPED, never thrown on. A missing rationale
 *      row must not blank the whole review panel.
 *
 * THE MONEY RULE
 * Nothing in this file does arithmetic on money, derives a dollar figure, or
 * fills in a price. Every dollar an estimator sees comes from /api/calculate.
 * ───────────────────────────────────────────────────────────────────────────
 */

import {
  createHardwareComponent,
  createLineItem,
  createMaterialComponent,
  createOperationComponent,
} from "../../../state/lineItems";

const MAX_COMPONENTS = 40;
const MAX_NAME_LENGTH = 120;
const MAX_EVIDENCE = 40;

/** The engine's finishing department key. NOT "paint" — see mapDepartment. */
const DEPARTMENTS = ["cnc", "cut", "fab", "finish"];

const CLASSIFICATION_PHRASES = {
  custom_fabrication: "custom fabrication",
  bought_out_profile: "bought-out profile",
};

const CONFIDENCE_LEVELS = ["high", "medium", "low"];

/* ── Coercion primitives ───────────────────────────────────────────────── */

/** A finite number at or above `min`, else `fallback`. Strings are parsed. */
function numberAtLeast(value, min, fallback) {
  const parsed = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed < min) return fallback;
  return parsed;
}

/** A trimmed string capped at `max`, else null. Never an object or array. */
function text(value, max) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.slice(0, max);
}

/**
 * The engine reads finishing hours under `finish`. A component that arrives
 * with `department: "paint"` — the historical corpus's word, and the one the
 * model is most likely to reach for — would be silently dropped by the costing
 * engine, so it is mapped rather than passed through.
 */
export function mapDepartment(value) {
  if (value === "paint") return "finish";
  return DEPARTMENTS.includes(value) ? value : "fab";
}

/* ── Components ────────────────────────────────────────────────────────── */

/** Dimensions on a material component: both sides present, or nothing. */
function componentDimensions(raw) {
  if (!raw || typeof raw !== "object") return null;
  const width = numberAtLeast(raw.width, 0, null);
  const length = numberAtLeast(raw.length, 0, null);
  if (width === null || length === null) return null;
  return { width, length };
}

/** Rip-cut spec: a rip width and a kerf, both real numbers or nothing. */
function componentRipCut(raw) {
  if (!raw || typeof raw !== "object") return null;
  const ripWidth = numberAtLeast(raw.rip_width, 0, null);
  if (ripWidth === null) return null;
  return { rip_width: ripWidth, kerf: numberAtLeast(raw.kerf, 0, 0.25) };
}

/**
 * One assembly component, built by the same factories the hand-entry path
 * uses — so a drafted component is indistinguishable from a typed one, and
 * ComponentSubRow can edit it without special cases.
 */
export function normalizeComponent(raw) {
  if (!raw || typeof raw !== "object") return null;

  if (raw.type === "operation") {
    return createOperationComponent({
      name: text(raw.name, MAX_NAME_LENGTH) || "Labor",
      hours_per_unit: numberAtLeast(raw.hours_per_unit, 0, 0),
      department: mapDepartment(raw.department),
    });
  }

  if (raw.type === "hardware") {
    return createHardwareComponent({
      name: text(raw.name, MAX_NAME_LENGTH) || "",
      unit_cost: numberAtLeast(raw.unit_cost, 0, 0),
      quantity_per_unit: numberAtLeast(raw.quantity_per_unit, 0, 1),
    });
  }

  return createMaterialComponent({
    name: text(raw.name, MAX_NAME_LENGTH) || "",
    unit: raw.unit === "sheet" ? "sheet" : "each",
    quantity_per_unit: numberAtLeast(raw.quantity_per_unit, 0, 1),
    unit_cost: numberAtLeast(raw.unit_cost, 0, 0),
    dimensions: componentDimensions(raw.dimensions),
    rip_cut: componentRipCut(raw.rip_cut),
  });
}

/* ── The line item ─────────────────────────────────────────────────────── */

/** The provenance stamp, whitelist-copied. Never accepted as pre-accepted. */
function normalizeProvenance(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    draft_id: text(raw.draft_id, 64) || "",
    model: text(raw.model, 80) || "",
    generated_at: text(raw.generated_at, 40) || "",
    manufacturing_version: text(raw.manufacturing_version, 20) || "",
    manufacturing_sha256: text(raw.manufacturing_sha256, 64) || "",
    // Landing a draft never accepts it. Only the estimator does that.
    accepted_at: null,
  };
}

/**
 * Build a line item from the draft's `line_item` payload.
 *
 * The result is Custom Mode by construction: an uncarded family has no rate
 * card, and custom mode is the path that prices from hand-entered components.
 */
export function normalizeDraftLineItem(rawLineItem) {
  const raw = rawLineItem && typeof rawLineItem === "object" ? rawLineItem : {};
  const base = createLineItem();

  const cncHours = numberAtLeast(raw.cnc_hours, 0, 0);
  const cutHours = numberAtLeast(raw.cut_hours, 0, 0);
  const fabHours = numberAtLeast(raw.fab_hours, 0, 0);
  const finishHours = numberAtLeast(raw.finish_hours, 0, 0);

  const components = (
    Array.isArray(raw.assembly_components) ? raw.assembly_components : []
  )
    .slice(0, MAX_COMPONENTS)
    .map(normalizeComponent)
    .filter(Boolean);

  return {
    ...base,

    mode: "custom",
    name: text(raw.name, MAX_NAME_LENGTH) || "AI-drafted assembly",
    assembly_components: components,
    manual_total_override: null,

    // A custom line owns no catalog product, product type or pinned sheet.
    product_id: null,
    product_type_key: null,
    material_id: null,
    material_option_key: null,
    material_option_label: null,
    material_selection_mode: "product_default",

    material_classification: text(raw.material_classification, 60) || "",
    finish_mode: text(raw.finish_mode, 40),
    finish_readiness: "",
    waste_mode: null,
    waste_custom_pct: null,

    length: numberAtLeast(raw.length, 0, 0),
    width: numberAtLeast(raw.width, 0, 0),
    height: numberAtLeast(raw.height, 0, 0),
    quantity: numberAtLeast(raw.quantity, 1, 1),
    reinforcement_needed: Boolean(raw.reinforcement_needed),

    additional_description: "",
    additional_units: 0,
    additional_cost_per_unit: 0,

    // The composer never prices. A unit cost from the model would be a price.
    unit_cost: 0,

    cnc_hours: cncHours,
    cut_hours: cutHours,
    fab_hours: fabHours,
    finish_hours: finishHours,
    // Rebuilt from the four fields above rather than copied, so an inconsistent
    // incoming mirror cannot make the editor and the payload disagree.
    fab_labor: {
      description: text(raw.fab_labor?.description, 200) || "TBD",
      cnc_hours: cncHours,
      cut_hours: cutHours,
      fab_hours: fabHours,
      paint_hours: finishHours,
    },

    ai_drafted: normalizeProvenance(raw.ai_drafted),
  };
}

/* ── The review payload ────────────────────────────────────────────────── */

function normalizePart(raw) {
  if (!raw || typeof raw !== "object") return null;
  const role = text(raw.role, 40);
  if (!role) return null;
  return { role, stock: text(raw.stock, 200), note: text(raw.note, 400) };
}

function normalizeDecomposition(raw) {
  if (!raw || typeof raw !== "object") return null;
  const classification = text(raw.classification, 40);
  return {
    classification,
    classificationPhrase: classification
      ? CLASSIFICATION_PHRASES[classification] ||
        classification.replace(/_/g, " ")
      : null,
    family: text(raw.family, 40),
    parts: (Array.isArray(raw.parts) ? raw.parts : [])
      .map(normalizePart)
      .filter(Boolean),
    method_summary: text(raw.method_summary, 800),
  };
}

function normalizeRationaleRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const statement = text(raw.statement, 240);
  if (!statement) return null;
  return {
    field: text(raw.field, 120),
    statement,
    evidence_ids: (Array.isArray(raw.evidence_ids) ? raw.evidence_ids : [])
      .map((id) => numberAtLeast(id, 0, null))
      .filter((id) => id !== null),
  };
}

/**
 * One cited historical line item.
 *
 * `unit_cost` is deliberately NOT carried through: the panel exists to justify
 * INPUTS — hours and stock — and a decades-old sheet price shown next to a
 * live draft would read as a quote. Hours only.
 */
function normalizeEvidenceRow(raw) {
  if (!raw || typeof raw !== "object") return null;
  const id = numberAtLeast(raw.id, 0, null);
  if (id === null) return null;
  return {
    id,
    source_file: text(raw.source_file, 200),
    job_number: text(raw.job_number, 20),
    indexed_at: text(raw.indexed_at, 40),
    name: text(raw.name, 200),
    description: text(raw.description, 400),
    quantity: numberAtLeast(raw.quantity, 0, null),
    labor_per_unit: numberAtLeast(raw.labor_per_unit, 0, null),
    cnc_hours: numberAtLeast(raw.cnc_hours, 0, null),
    cut_hours: numberAtLeast(raw.cut_hours, 0, null),
    fab_hours: numberAtLeast(raw.fab_hours, 0, null),
    paint_hours: numberAtLeast(raw.paint_hours, 0, null),
    material_type: text(raw.material_type, 60),
    dimensions_raw: text(raw.dimensions_raw, 120),
    confidence: numberAtLeast(raw.confidence, 0, null),
  };
}

function normalizeConfidence(raw) {
  if (!raw || typeof raw !== "object") return [];
  return Object.entries(raw)
    .map(([field, entry]) => {
      const level = entry && typeof entry === "object" ? entry.level : null;
      if (!CONFIDENCE_LEVELS.includes(level)) return null;
      return {
        field: text(field, 120) || "draft",
        level,
        reason: text(entry.reason, 200),
      };
    })
    .filter(Boolean);
}

/**
 * Warnings are review copy, not money. Most are plain strings; a structured
 * `{ message }` record is flattened so a future kind field cannot vanish just
 * because the panel still renders text nodes.
 */
const MAX_WARNING_LENGTH = 280;

function normalizeWarning(raw) {
  if (typeof raw === "string") return text(raw, MAX_WARNING_LENGTH);
  if (!raw || typeof raw !== "object") return null;
  return text(raw.message, MAX_WARNING_LENGTH);
}

/** Server-authored stale-price hint. The dollar figure is the copied cost. */
export function isPriceAgeWarning(warning) {
  return typeof warning === "string" && warning.startsWith("Unit cost $");
}

function normalizeCandidate(raw) {
  if (!raw || typeof raw !== "object") return null;
  const category = text(raw.category, 50);
  if (!category) return null;
  return {
    category,
    labor_basis: text(raw.labor_basis, 20) || "sqft",
    geometry_method: text(raw.geometry_method, 40) || "flat_2d",
    band_label: text(raw.band_label, 40),
    size_band_dim: text(raw.size_band_dim, 20),
    size_band_min_in: numberAtLeast(raw.size_band_min_in, 0, null),
    size_band_max_in: numberAtLeast(raw.size_band_max_in, 0, null),
    cnc_per_sqft: numberAtLeast(raw.cnc_per_sqft, 0, 0),
    cut_per_sqft: numberAtLeast(raw.cut_per_sqft, 0, 0),
    fab_per_sqft: numberAtLeast(raw.fab_per_sqft, 0, 0),
    paint_per_sqft: numberAtLeast(raw.paint_per_sqft, 0, 0),
    family_uncarded: raw.family_uncarded === true,
  };
}

/**
 * The whole draft response, normalized.
 *
 * Everything except `lineItem` is review material — it lives in workbench
 * component state, not in the JobVersion. The permanent record is the badge,
 * the numbers and the provenance stamp on the line itself.
 */
export function normalizeDraft(raw) {
  const source = raw && typeof raw === "object" ? raw : {};
  const lineItem = normalizeDraftLineItem(source.line_item);
  const grounding =
    source.grounding && typeof source.grounding === "object"
      ? source.grounding
      : {};

  return {
    lineItem,
    draftId: lineItem.ai_drafted?.draft_id || text(source.draft_id, 64) || "",
    model: lineItem.ai_drafted?.model || text(source.model, 80) || "",
    generatedAt:
      lineItem.ai_drafted?.generated_at || text(source.generated_at, 40) || "",
    manufacturingVersion:
      lineItem.ai_drafted?.manufacturing_version ||
      text(grounding.manufacturing_version, 20) ||
      "",
    decomposition: normalizeDecomposition(source.decomposition),
    rationale: (Array.isArray(source.rationale) ? source.rationale : [])
      .map(normalizeRationaleRow)
      .filter(Boolean),
    evidence: (Array.isArray(source.evidence) ? source.evidence : [])
      .slice(0, MAX_EVIDENCE)
      .map(normalizeEvidenceRow)
      .filter(Boolean),
    confidence: normalizeConfidence(source.confidence),
    warnings: (Array.isArray(source.warnings) ? source.warnings : [])
      .map(normalizeWarning)
      .filter(Boolean),
    rateCardCandidate: normalizeCandidate(source.rate_card_candidate),
  };
}

/**
 * The accept-candidate request body.
 *
 * `family_uncarded` is a server-computed read-out, not something the client
 * asserts, so it is dropped on the way back.
 */
export function buildCandidatePayload(draft, { jobName, lineName } = {}) {
  const candidate = draft?.rateCardCandidate;
  if (!candidate) return null;
  const { family_uncarded: _uncarded, ...fields } = candidate;

  return {
    draft_id: draft.draftId,
    candidate: fields,
    context: {
      job_name: text(jobName, 200),
      line_name: text(lineName, 200),
      evidence_ids: (draft.evidence || []).map((row) => row.id),
      rationale: (draft.rationale || []).map((row) => row.statement),
    },
  };
}

/* ── Predicates the UI reads ───────────────────────────────────────────── */

/** Drafted by AI at some point — badge forever, including after acceptance. */
export function isAiDrafted(lineItem) {
  return Boolean(lineItem?.ai_drafted);
}

/** Drafted and NOT yet signed off. This is the state that blocks confidence. */
export function isAiDraftPending(lineItem) {
  return Boolean(lineItem?.ai_drafted) && !lineItem.ai_drafted.accepted_at;
}
